import { initContract } from "@ts-rest/core";
import { format, parseISO } from "date-fns";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { BankCashItemType } from "@common/types/bankCashItem";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { entityIdsQuerySchema } from "@common/schemas/entity-ids-query.schema";

const TransactionForReconciliationSchema = z.object({
    date: z.string().date(),
    bankDescription: z.string(),
    amount: z.number(),
});
export type TransactionForReconciliation = z.infer<
    typeof TransactionForReconciliationSchema
>;

export enum ReconciliationUnmatchedBehavior {
    REMOVE = "REMOVE",
    IGNORE = "IGNORE",
    NOT_RECONCILED = "NOT_RECONCILED",
    FROM_PREVIOUS_PERIOD = "FROM_PREVIOUS_PERIOD",
}

const TransactionForReconciliationFromAccountSchema =
    TransactionForReconciliationSchema.extend({
        financialTransactionId: z.number(),
        unmatchedBehavior: z.nativeEnum(ReconciliationUnmatchedBehavior),
        isCashItem: z.boolean().optional(),
    });
export type TransactionForReconciliationFromAccount = z.infer<
    typeof TransactionForReconciliationFromAccountSchema
>;

const TransactionForReconciliationFromStatementSchema =
    TransactionForReconciliationSchema.extend({
        financialDocumentId: z.number(),
        row: z.number(),
    });
export type TransactionForReconciliationFromStatement = z.infer<
    typeof TransactionForReconciliationFromStatementSchema
>;

const TransactionsPairForReconciliationSchema = z.object({
    fromStatement: TransactionForReconciliationFromStatementSchema.optional(),
    fromAccount: TransactionForReconciliationFromAccountSchema.optional(),
});
export type TransactionsPairForReconciliation = z.infer<
    typeof TransactionsPairForReconciliationSchema
>;

/**
 * A check or deposit that has been posted but that no bank evidence has
 * cleared yet. Not tied to the statement period: either can stay outstanding
 * for months, so these are listed separately from the period's pairs.
 */
const OutstandingCashItemSchema = TransactionForReconciliationSchema.extend({
    financialTransactionId: z.number(),
    type: z.nativeEnum(BankCashItemType),
    checkNumber: z.string().nullable(),
});
export type OutstandingCashItem = z.infer<typeof OutstandingCashItemSchema>;

export const ReconciliationSummaryResponseSchema = z.object({
    transactionsPairs: z.array(TransactionsPairForReconciliationSchema),
    outstandingCashItems: z.array(OutstandingCashItemSchema),
    kickOpeningBalanceDifference: z.number(),
    kickOpeningDate: z.string().date(),
    kickOpeningBalance: z.number().nullable(),
    statementsOpeningBalance: z.number(),
    statementsOpeningDate: z.string().date(),
    statementsClosingBalance: z.number(),
    statementsClosingDate: z.string().date(),
    additionalDays: z.number(),
    isBalanceReconciliation: z.boolean(),
});
export type ReconciliationSummaryResponse = z.infer<
    typeof ReconciliationSummaryResponseSchema
>;

export enum ReconciliationErrorCodes {
    STATEMENT_DATE_OR_BALANCE_MISSING = "statement_date_or_balance_missing",
    NON_CONSECUTIVE_STATEMENTS_DATES = "non_consecutive_statements_dates",
    NON_CONSECUTIVE_STATEMENTS_BALANCES = "non_consecutive_statements_balances",
    RECONCILING_WRONG_DATES = "reconciling_wrong_dates",
    STATEMENT_PERIOD_ALREADY_RECONCILED = "statement_period_already_reconciled",
    NO_DOCUMENTS_FOUND = "no_documents_found",
    ENTITY_NOT_SUPPORTED_FOR_RECONCILIATION = "entity_not_supported_for_reconciliation",
    RECONCILIATION_IN_PROGRESS = "reconciliation_in_progress",
    TRANSACTION_AFTER_CLOSING_DATE = "transaction_after_closing_date",
    PERIOD_LOCKED = "period_locked",
}

export const ENTITY_NOT_SUPPORTED_FOR_RECONCILIATION_MESSAGE =
    "Account not available for reconciliation. Complete the entity setup first";

export const RECONCILIATION_IN_PROGRESS_MESSAGE =
    "Bank reconciliation is still processing. Please wait a few minutes before trying again.";

export const RECONCILIATION_TRANSACTION_AFTER_CLOSING_DATE_MESSAGE =
    "Reconciled transactions cannot be dated after the statement closing date";

export const RECONCILIATION_TRANSACTIONS_CHANGED_MESSAGE =
    "Some transactions changed since this reconciliation was loaded. Reload the reconciliation and try again.";

export const RECONCILIATION_PERIOD_LOCKED_MESSAGE =
    "This transaction is in a reconciled or locked period. Unreconcile or unlock that period first, then correct it there.";

function describeStatementLine({
    date,
    amount,
    bankDescription,
}: TransactionForReconciliation) {
    const formattedDate = format(parseISO(date), fullDateFullYearFormat);
    return `${bankDescription} on ${formattedDate} for ${amount}`;
}

export function getStatementLineAlreadySavedMessage(
    line: TransactionForReconciliation,
) {
    return `${describeStatementLine(line)} is already saved as a transaction. Reload the reconciliation and try again.`;
}

export function getStatementLineNotOnStatementMessage(
    line: TransactionForReconciliation,
) {
    return `${describeStatementLine(line)} is not on the statements being reconciled.`;
}

interface StatementPeriodAlreadyReconciledMessageParams {
    fileName: string;
    statementClosingDate: string;
    lastReconciledClosingDate: string;
}

export function getStatementPeriodAlreadyReconciledMessage({
    fileName,
    statementClosingDate,
    lastReconciledClosingDate,
}: StatementPeriodAlreadyReconciledMessageParams) {
    const formattedLastReconciledDate = format(
        parseISO(lastReconciledClosingDate),
        fullDateFullYearFormat,
    );
    const formattedStatementClosingDate = format(
        parseISO(statementClosingDate),
        fullDateFullYearFormat,
    );
    return `${fileName} covers a period already reconciled through ${formattedLastReconciledDate} (statement closes ${formattedStatementClosingDate}). Remove it from the queue before continuing.`;
}

export function buildStatementPeriodAlreadyReconciledErrorBody({
    fileName,
    statementClosingDate,
    lastReconciledClosingDate,
}: StatementPeriodAlreadyReconciledMessageParams): ReconciliationStatementPeriodAlreadyReconciledError {
    return {
        errorCode: ReconciliationErrorCodes.STATEMENT_PERIOD_ALREADY_RECONCILED,
        fileName,
        statementClosingDate,
        lastReconciledClosingDate,
        message: getStatementPeriodAlreadyReconciledMessage({
            fileName,
            statementClosingDate,
            lastReconciledClosingDate,
        }),
    };
}

export interface ReconciliationIncosistentStateError {
    errorCode: ReconciliationErrorCodes.RECONCILING_WRONG_DATES;
    expectedStartDate: string;
    receivedEndDate: string;
}

export interface ReconciliationMissingDateOrBalanceError {
    errorCode: ReconciliationErrorCodes.STATEMENT_DATE_OR_BALANCE_MISSING;
    fileName: string;
}

export interface ReconciliationNonConsecutiveStatementsDatesError {
    errorCode: ReconciliationErrorCodes.NON_CONSECUTIVE_STATEMENTS_DATES;
    previousStatementFileName: string;
    previousStatementClosingDate: string;
    currentStatementFileName: string;
    currentStatementOpeningDate: string;
}

export interface ReconciliationNonConsecutiveStatementsBalancesError {
    errorCode: ReconciliationErrorCodes.NON_CONSECUTIVE_STATEMENTS_BALANCES;
    previousStatementFileName: string;
    previousStatementClosingBalance: number;
    currentStatementFileName: string;
    currentStatementOpeningBalance: number;
}

export interface ReconciliationStatementPeriodAlreadyReconciledError {
    errorCode: ReconciliationErrorCodes.STATEMENT_PERIOD_ALREADY_RECONCILED;
    fileName: string;
    statementClosingDate: string;
    lastReconciledClosingDate: string;
    message: string;
}

export interface ReconciliationNoDocumentsFoundError {
    errorCode: ReconciliationErrorCodes.NO_DOCUMENTS_FOUND;
}

export interface ReconciliationEntityNotSupportedError {
    errorCode: ReconciliationErrorCodes.ENTITY_NOT_SUPPORTED_FOR_RECONCILIATION;
    message: string;
}

export interface ReconciliationInProgressError {
    errorCode: ReconciliationErrorCodes.RECONCILIATION_IN_PROGRESS;
    message: string;
}

export interface ReconciliationTransactionAfterClosingDateResponse {
    errorCode: ReconciliationErrorCodes.TRANSACTION_AFTER_CLOSING_DATE;
    message: string;
}

export interface ReconciliationPeriodLockedError {
    errorCode: ReconciliationErrorCodes.PERIOD_LOCKED;
    message: string;
    lockedEntities: UndoReconciliationLockedEntity[];
}

export const UndoReconciliationLockedEntitySchema = z.object({
    entityId: z.number(),
    entityName: z.string(),
    lockDate: z.string().date(),
    transactionCount: z.number(),
});
export type UndoReconciliationLockedEntity = z.infer<
    typeof UndoReconciliationLockedEntitySchema
>;

export const UndoReconciliationLockBlockersResponseSchema = z.object({
    lockedEntities: z.array(UndoReconciliationLockedEntitySchema),
});
export type UndoReconciliationLockBlockersResponse = z.infer<
    typeof UndoReconciliationLockBlockersResponseSchema
>;

export const BankReconciliationSchema = z.object({
    id: z.string().uuid(),
    financialAccountId: z.number(),
    openingDate: z.string().date(),
    closingDate: z.string().date(),
    openingBalance: z.number(),
    closingBalance: z.number().nullable(),
});

export type BankReconciliation = z.infer<typeof BankReconciliationSchema>;

const StatementTransactionToAddSchema = z.object({
    financialDocumentId: z.number(),
    amount: z.number(),
    date: z.string().date(),
    bankDescription: z.string(),
});
export type StatementTransactionToAdd = z.infer<
    typeof StatementTransactionToAddSchema
>;

const CheckMergeSchema = z.union([
    z.object({
        checkTransactionId: z.number(),
        bankTransactionId: z.number(),
    }),
    z.object({
        checkTransactionId: z.number(),
        statementLine: StatementTransactionToAddSchema,
    }),
]);
export type CheckMerge = z.infer<typeof CheckMergeSchema>;

const c = initContract();
export const reconciliationContract = c.router(
    {
        getReconciliationSummary: {
            method: "GET",
            path: "/summary",
            query: z.object({
                financialAccountId: z.coerce.number(),
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: ReconciliationSummaryResponseSchema,
                400: c.type<
                    | ReconciliationIncosistentStateError
                    | ReconciliationMissingDateOrBalanceError
                    | ReconciliationNonConsecutiveStatementsDatesError
                    | ReconciliationNonConsecutiveStatementsBalancesError
                    | ReconciliationNoDocumentsFoundError
                    | ReconciliationEntityNotSupportedError
                    | ReconciliationStatementPeriodAlreadyReconciledError
                >(),
            },
        },
        reconcileTransactions: {
            method: "POST",
            path: "/reconcile",
            body: z.object({
                financialAccountId: z.number(),
                workspaceId: z.string().uuid(),
                financialTransactionsToRemove: z.array(z.number()),
                statementTransactionsToAdd: z.array(
                    StatementTransactionToAddSchema,
                ),
                reconciledTransactions: z.array(
                    z.object({
                        financialTransactionId: z.number(),
                        newDate: z.string().date().optional(),
                        clearedDate: z.string().date().optional(),
                    }),
                ),
                /**
                 * Outstanding checks the user paired with a bank transaction
                 * already in the books, or with a statement line this call
                 * creates. The check survives the merge and the bank
                 * transaction is absorbed into it.
                 */
                checkMerges: z.array(CheckMergeSchema).max(500).optional(),
            }),
            responses: {
                200: c.noBody(),
                400: c.type<
                    | ReconciliationStatementPeriodAlreadyReconciledError
                    | ReconciliationTransactionAfterClosingDateResponse
                    | z.infer<typeof ErrorMessageSchema>
                >(),
                409: c.type<
                    | ReconciliationInProgressError
                    | ReconciliationPeriodLockedError
                >(),
            },
        },
        getBankReconciliations: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                entityIds: entityIdsQuerySchema,
            }),
            responses: {
                200: z.array(BankReconciliationSchema),
                404: ErrorMessageSchema,
            },
        },
        deleteLastBankReconciliation: {
            method: "DELETE",
            path: "/:entityId/:financialAccountId",
            pathParams: z.object({
                entityId: z.coerce.number(),
                financialAccountId: z.coerce.number(),
            }),
            responses: {
                200: BankReconciliationSchema,
                400: ErrorMessageSchema,
                409: c.type<
                    | ReconciliationInProgressError
                    | ReconciliationPeriodLockedError
                >(),
            },
        },
        getUndoReconciliationLockBlockers: {
            method: "GET",
            path: "/:entityId/:financialAccountId/undo-lock-blockers",
            pathParams: z.object({
                entityId: z.coerce.number(),
                financialAccountId: z.coerce.number(),
            }),
            responses: {
                200: UndoReconciliationLockBlockersResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/bank-reconciliation",
    },
);
